import { Avatar as AvatarPrimitive } from "@base-ui/react/avatar";

import { cn } from "../../lib/utils";

/**
 * Migrated from `@radix-ui/react-avatar` to Base UI.
 *
 * The anatomy is unchanged — `Root`/`Image`/`Fallback`, same elements (`span`,
 * `img`, `span`), same rule for which part is mounted: the image once it has
 * decoded, the fallback until then and forever if it errors. So this migration
 * is confined to the import and the prop types; every class below is the one
 * that was there before, and the committed screenshot baselines pin that.
 *
 * Two prop-level differences, both handled here rather than at the call sites:
 *
 *  - Base UI types `className` and `style` as `T | ((state) => T)`, so the
 *    state-driven callback form can style off `imageLoadingStatus` without a
 *    wrapper. `cn()` takes `ClassValue` and would silently drop a function, so
 *    `className` is narrowed back to a string on every part. Callers only ever
 *    pass strings; the callback form stays reachable through `render` if a
 *    future caller genuinely needs it.
 *  - `Fallback`'s delay prop is `delay`, not Radix's `delayMs`. No caller in
 *    this repo passes either (checked across `frontend/` and `packages/`), so
 *    the upstream name is adopted directly instead of shimming the old one —
 *    a shim here would be permanent drift bought to serve zero callers.
 *
 * The `render` prop replaces Radix's `asChild` and is inherited from the
 * primitive's props, unused by any current caller.
 */

/** Base UI's props for one part, with `className` narrowed back to a string. */
type PartProps<P> = Omit<P, "className"> & { className?: string };

function Avatar({
  className,
  ...props
}: PartProps<AvatarPrimitive.Root.Props>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        "relative flex size-8 shrink-0 overflow-hidden rounded-full",
        className,
      )}
      {...props}
    />
  );
}

function AvatarImage({
  className,
  ...props
}: PartProps<AvatarPrimitive.Image.Props>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn("aspect-square size-full", className)}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: PartProps<AvatarPrimitive.Fallback.Props>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "bg-muted flex size-full items-center justify-center rounded-full",
        className,
      )}
      {...props}
    />
  );
}

export { Avatar, AvatarImage, AvatarFallback };
